import { useCallback } from 'react';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { APIError } from '@interxfi/api-client/api-error';
import { useIxcRegistrationV1ProfileCommandServiceCreateProfile } from '@interxfi/api-client/generated/hooks/ixc-registration-v1-profile-command-service-create-profile';
import { ActionLayout } from '@interxfi/app-components/components/layout/action-layout/action-layout';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { useButtonState } from '@interxfi/design-system/components/button/button-state';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import { TableOfContentsContextProvider } from '@interxfi/design-system/components/table-of-contents/table-of-contents-context';
import { useAddToast } from '@interxfi/design-system/components/toast/toast-context';
import { Typography } from '@interxfi/design-system/components/typography';
import { useBreakpoint } from '@interxfi/design-system/util/responsive';
import type { IxcRegistrationV1ProfileTypeOneOfValue } from '@interxfi/types/generated/api';
import { ProfileFormReview } from '../../../../../../../../../features/registration/profile/form/profile-form-review';
import {
  type PendingProfileValues,
  useProfileFormSubmissionContext,
  useResetProfileFormSubmissionContext,
  useResetProfileFormSubmissionContextOnNavigateAway,
} from '../../../../../../../../../features/registration/profile/form/profile-form-submission-context';
import { RegistrationProfileFormTableOfContents } from '../../../../../../../../../features/registration/profile/form/registration-profile-form-table-of-contents';
import {
  getRegistrationProfileCreatePath,
  getRegistrationProfileDetailPath,
} from '../../../../../../../../../features/registration/profile/registration-profile-routing';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../util/error-handler';

interface WithPendingProfile {
  pendingProfile: PendingProfileValues;
}

export const Route = createFileRoute('/_app/_clearing-core/registration/profile/_profile/create/$profileType/_create/review')({
  loader: ({ params }) => {
    const profileType = params.profileType as IxcRegistrationV1ProfileTypeOneOfValue;
    const { pendingProfile } = useProfileFormSubmissionContext.getState();

    if (!pendingProfile?.spec || !(profileType in pendingProfile.spec)) {
      throw redirect({ to: '/registration/profile/create/$profileType', params: { profileType } });
    }

    return {
      pendingProfile,
      breadcrumb: buildBreadcrumb({
        id: `create-profile-${profileType}-review`,
        label: 'Review',
        to: `/registration/profile/create/${profileType}/review`,
      }),
    } satisfies AggregateBreadcrumb & WithPendingProfile;
  },
  preload: false,
  shouldReload: true,
  component: CreateProfileReview,
});

const rightColumnContainer = {
  width: { type: 'fixed', width: 320 },
} as const;

const defaultErrorMessageGetter = buildDefaultErrorMessageGetter('Failed to create profile', 'create a profile');

// organization profile must have at least one officer or director
function errorMessageGetter(error: Error | null): string | undefined {
  if (!error) {
    return undefined;
  }

  if (error instanceof APIError) {
    if (error.message.toLowerCase().includes('organization profile must have at least one officer or director')) {
      return 'An Organization Profile must have at least one officer or director.';
    }
  }

  return defaultErrorMessageGetter(error);
}

function CreateProfileReview() {
  const { profileType } = Route.useParams();
  const navigate = Route.useNavigate();
  useResetProfileFormSubmissionContextOnNavigateAway(getRegistrationProfileCreatePath(profileType));
  const { pendingProfile } = Route.useLoaderData();
  const addToast = useAddToast();
  const { mutateAsync, isPending, error } = useIxcRegistrationV1ProfileCommandServiceCreateProfile();
  useErrorHandler(error, errorMessageGetter);
  const [submitState, submitRef] = useButtonState(isPending, error);
  const reset = useResetProfileFormSubmissionContext();
  const isTooCompactForTableOfContents = useBreakpoint('sm');

  const handleSubmit = useCallback(async () => {
    const result = await mutateAsync({ programId: pendingProfile.programId, spec: pendingProfile.spec });

    if (result) {
      addToast({ id: 'profileCreated', content: 'Profile was created', variant: 'success' });

      reset();

      const navigateTo = getRegistrationProfileDetailPath(result.profile.profileId);
      if (navigateTo) {
        await navigate({ to: navigateTo });
      }
    }
  }, [pendingProfile, addToast, mutateAsync, navigate, reset]);

  const baseReviewElements = (
    <VerticalStack gap={4}>
      <Typography.Level1>Review Information</Typography.Level1>
      <VerticalStack gap={7}>
        <ProfileFormReview pendingProfile={pendingProfile.spec} programId={pendingProfile.programId} />
      </VerticalStack>
    </VerticalStack>
  );

  return (
    <TableOfContentsContextProvider>
      <ActionLayout
        actions={[
          {
            buttonLink: { children: 'Back', id: 'back', LinkElement: Link, to: getRegistrationProfileCreatePath(profileType), variant: 'secondary' },
          },
          { button: { children: 'Create Profile', id: 'submit', onClick: handleSubmit, ref: submitRef, state: submitState, variant: 'primary' } },
        ]}
        maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}
      >
        {isTooCompactForTableOfContents ? (
          baseReviewElements
        ) : (
          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: baseReviewElements,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...rightColumnContainer,
              render: (isWrapping) =>
                isWrapping ? null : (
                  <RegistrationProfileFormTableOfContents selectedProfileType={profileType as IxcRegistrationV1ProfileTypeOneOfValue} />
                ),
            }}
            wrap="forward"
          />
        )}
      </ActionLayout>
    </TableOfContentsContextProvider>
  );
}
